<template>
  <div
      class="d-flex flex-column card mb-10"
      :style="cardStyles"
      :key="item.id"
  >
    <!-- Top Header Bar -->
    <div class="card-header" :style="headerBarStyles"></div>

    <!-- Title Section -->
    <div v-if="!hideTitle" class="title-section" :style="titleSectionStyles">
      <span class="title-text" :style="titleStyles">
        {{ item.dsc_titulo_enquete }}
      </span>
    </div>

    <!-- Poll Content Section - Horizontal Layout Only -->
    <div class="poll-layout" :style="pollLayoutStyles">
      <div class="horizontal-poll-container">
        <!-- Left Option with progress bar and percentage on the left side -->
        <div class="option-container left-container" :style="containerStyles">
          <transition name="results-transition" mode="out-in">
            <!-- Show results only if flg_exibir_resultado is true -->
            <div v-if="item.flg_exibir_resultado" key="with-results" class="progress-row" :style="progressRowStyles">
              <transition name="percentage-fade" appear>
                <div class="percentage-text left-percentage" :style="leftPercentageStyles">
                  {{ animatedLeftPercentage }}%
                </div>
              </transition>
              <div class="progress-bar-section" :style="progressBarSectionStyles">
                <div v-if="!hideOptionTitle" class="option-text" :style="leftOptionTextStyles">{{ leftOption.dsc_titulo }}</div>
                <div class="option-progress-bar" :style="progressContainerStyles">
                  <div class="progress-fill" :style="leftProgressFillStyles"></div>
                </div>
              </div>
            </div>
            <!-- Show only option text if flg_exibir_resultado is false -->
            <div v-else key="without-results" class="option-only-row" :style="optionOnlyRowStyles">
              <div v-if="!hideOptionTitle" class="option-text-only" :style="leftOptionOnlyTextStyles">{{ leftOption.dsc_titulo }}</div>
            </div>
          </transition>
        </div>

        <!-- Right Option with progress bar and percentage on the right side -->
        <div class="option-container right-container" :style="containerStyles">
          <transition name="results-transition" mode="out-in">
            <!-- Show results only if flg_exibir_resultado is true -->
            <div v-if="item.flg_exibir_resultado" key="with-results" class="progress-row" :style="progressRowStyles">
              <div class="progress-bar-section" :style="progressBarSectionStyles">
                <div v-if="!hideOptionTitle" class="option-text" :style="rightOptionTextStyles">{{ rightOption.dsc_titulo }}</div>
                <div class="option-progress-bar" :style="progressContainerStyles">
                  <div class="progress-fill progress-fill-right" :style="rightProgressFillStyles"></div>
                </div>
              </div>
              <transition name="percentage-fade" appear>
                <div class="percentage-text right-percentage" :style="rightPercentageStyles">
                  {{ animatedRightPercentage }}%
                </div>
              </transition>
            </div>
            <!-- Show only option text if flg_exibir_resultado is false -->
            <div v-else key="without-results" class="option-only-row" :style="optionOnlyRowStyles">
              <div v-if="!hideOptionTitle" class="option-text-only" :style="rightOptionOnlyTextStyles">{{ rightOption.dsc_titulo }}</div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Bottom Footer Bar -->
    <div class="card-footer" :style="footerBarStyles"></div>
  </div>
</template>

<script>
export default {
  props: {
    item: Object,
    posicaoEnquete: String,
    hideTitle: Boolean,
    hideOptionTitle: Boolean,
    isVerticalBar: Boolean,
  },

  data() {
    return {
      saiaJustaTheme: {
        colors: {
          cardBg: "rgb(188,153,200)",
          titleColor: "#631C43",
          labelColor: "#FBF9EA",
          percentageColor: "#FBF9EA",
          progressBar: "#631C43",
          progressBg: "rgb(250,247,232)",
          headerFooterBg: "#48210D",
          gradientStart: "#631C43",
          gradientEnd: "#FBF9EA",
        },
        typography: {
          titleFont: "Fields Regular",
          labelFont: "Fields Medium",
        },
      },
      animatedLeftPercentage: 0,
      animatedRightPercentage: 0,
      animatedProgressLeft: 0,
      animatedProgressRight: 0,
      animationDuration: 1200,
      previousResultsVisible: false,
    };
  },

  computed: {
    screenWidth() {
      return this.$vuetify.breakpoint.width;
    },

    screenHeight() {
      return this.$vuetify.breakpoint.height;
    },

    responsiveScale() {
      const baseWidth = 1920;
      const baseHeight = 1080;
      const widthScale = this.screenWidth / baseWidth;
      const heightScale = this.screenHeight / baseHeight;
      const scale = Math.min(widthScale, heightScale);
      return Math.max(0.5, Math.min(1.2, scale));
    },

    responsiveLayout() {
      const scale = this.responsiveScale;

      return {
        cardWidth: Math.round(1500 * scale),
        cardHeight: Math.round(280 * scale),
        titleFontSize: Math.round(45 * scale),
        labelFontSize: Math.round(30 * scale),
        percentageFontSize: Math.round(45 * scale),
        progressBarHeight: Math.round(38 * scale),
        percentageWidth: Math.round(180 * scale),
        gap: Math.round(20 * scale),
        margin: Math.round(50 * scale),
        optionTextGap: Math.round(14 * scale),
        padding: Math.round(40 * scale),
      };
    },

    currentTheme() {
      return this.saiaJustaTheme;
    },

    leftOption() {
      return this.item?.enquete_opcoes?.[0] || { dsc_titulo: '', resultado: 0 };
    },

    rightOption() {
      return this.item?.enquete_opcoes?.[1] || { dsc_titulo: '', resultado: 0 };
    },

    headerBarStyles() {
      return {
        width: "100%",
        height: "2px",
        backgroundColor: this.currentTheme.colors.headerFooterBg,
        flexShrink: 0,
      };
    },

    footerBarStyles() {
      return {
        width: "100%",
        height: "2px",
        backgroundColor: this.currentTheme.colors.headerFooterBg,
        flexShrink: 0,
      };
    },

    cardStyles() {
      const position = this.cardPositionStyles;
      const backgroundStyle = `background-color: ${this.currentTheme.colors.cardBg}`;
      const sizeStyles = `width: ${this.responsiveLayout.cardWidth}px; height: ${this.responsiveLayout.cardHeight}px`;
      const layoutStyles = "display: flex; flex-direction: column; justify-content: space-between; align-items: center; padding: 0";

      return [position, backgroundStyle, sizeStyles, layoutStyles]
          .filter((style) => style)
          .join("; ");
    },

    cardPositionStyles() {
      const positionMap = {
        esquerda: `justify-self: flex-start; margin-left: ${this.responsiveLayout.margin}px;`,
        centro: "justify-self: center",
        direita: `justify-self: flex-end; margin-right: ${this.responsiveLayout.margin}px;`,
      };
      return positionMap[this.posicaoEnquete] || "";
    },

    titleSectionStyles() {
      return {
        width: "100%",
        textAlign: "center",
        marginBottom: `${Math.round(20 * this.responsiveScale)}px`,
        marginTop: `${Math.round(20 * this.responsiveScale)}px`,
        padding: `0 ${this.responsiveLayout.padding}px`,
      };
    },

    titleStyles() {
      return {
        color: this.currentTheme.colors.titleColor,
        fontFamily: `${this.currentTheme.typography.titleFont} !important`,
        fontSize: `${this.responsiveLayout.titleFontSize}px`,
        fontWeight: "400",
        lineHeight: "1.2",
        display: "block",
      };
    },

    pollLayoutStyles() {
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: `${Math.round(1400 * this.responsiveScale)}px`,
        gap: `${this.responsiveLayout.gap}px`,
        flex: "1",
        padding: `${Math.round(40 * this.responsiveScale)}px 0 0`,
      };
    },

    containerStyles() {
      return {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        maxWidth: "none",
        minWidth: "0",
        width: "50%",
      };
    },

    progressRowStyles() {
      return {
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: `${this.responsiveLayout.gap}px`,
      };
    },

    progressBarSectionStyles() {
      return {
        flex: "1",
        position: "relative",
        minWidth: "0",
        width: "100%",
      };
    },

    leftOptionTextStyles() {
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "400",
        lineHeight: "1.2",
        wordBreak: "break-word",
        textAlign: "left",
        position: "absolute",
        bottom: `calc(100% + ${this.responsiveLayout.optionTextGap}px)`,
        left: "0",
        right: "0",
        zIndex: 2,
      };
    },

    rightOptionTextStyles() {
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "400",
        lineHeight: "1.2",
        wordBreak: "break-word",
        textAlign: "right",
        position: "absolute",
        bottom: `calc(100% + ${this.responsiveLayout.optionTextGap}px)`,
        left: "0",
        right: "0",
        zIndex: 2,
      };
    },

    progressContainerStyles() {
      return {
        flex: "1",
        height: `${this.responsiveLayout.progressBarHeight}px`,
        backgroundColor: this.currentTheme.colors.progressBg,
        borderRadius: "0px",
        overflow: "hidden",
        position: "relative",
        width: "100%",
        minWidth: "0",
      };
    },

    leftProgressFillStyles() {
      return {
        width: `${this.animatedProgressLeft}%`,
        height: "100%",
        background: this.animatedProgressLeft > 0 ? `linear-gradient(to right, ${this.currentTheme.colors.gradientStart} 40%, ${this.currentTheme.colors.gradientEnd} 100%)` : 'transparent',
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.8s ease",
        borderRadius: "0px",
      };
    },

    rightProgressFillStyles() {
      return {
        width: `${this.animatedProgressRight}%`,
        height: "100%",
        background: this.animatedProgressRight > 0 ? `linear-gradient(to left, ${this.currentTheme.colors.gradientStart} 40%, ${this.currentTheme.colors.gradientEnd} 100%)` : 'transparent',
        transition: "width 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.8s ease",
        borderRadius: "0px",
      };
    },

    leftPercentageStyles() {
      return {
        color: this.currentTheme.colors.percentageColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.percentageFontSize}px`,
        fontWeight: "400",
        width: `${this.responsiveLayout.percentageWidth}px`,
        flexShrink: 0,
        flexGrow: 0,
        zIndex: 3,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        textAlign: "right",
      };
    },

    rightPercentageStyles() {
      return {
        color: this.currentTheme.colors.percentageColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.percentageFontSize}px`,
        fontWeight: "400",
        width: `${this.responsiveLayout.percentageWidth}px`,
        flexShrink: 0,
        flexGrow: 0,
        zIndex: 3,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        textAlign: "left",
      };
    },

    optionOnlyRowStyles() {
      return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: `${this.responsiveLayout.progressBarHeight + 20}px`,
      };
    },

    leftOptionOnlyTextStyles() {
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "400",
        wordBreak: "break-word",
        textAlign: "center",
        width: "100%",
        padding: `0 ${this.responsiveLayout.padding}px`,
      };
    },

    rightOptionOnlyTextStyles() {
      return {
        color: this.currentTheme.colors.labelColor,
        fontFamily: `${this.currentTheme.typography.labelFont} !important`,
        fontSize: `${this.responsiveLayout.labelFontSize}px`,
        fontWeight: "400",
        wordBreak: "break-word",
        textAlign: "center",
        width: "100%",
        padding: `0 ${this.responsiveLayout.padding}px`,
      };
    },
  },

  watch: {
    'item.flg_exibir_resultado': {
      handler(newVal) {
        if (newVal && !this.previousResultsVisible) {
          this.animateValue('animatedLeftPercentage', 0, this.leftOption.resultado || 0);
          this.animateValue('animatedProgressLeft', 0, this.leftOption.resultado || 0);
          this.animateValue('animatedRightPercentage', 0, this.rightOption.resultado || 0);
          this.animateValue('animatedProgressRight', 0, this.rightOption.resultado || 0);
        }
        else if (!newVal) {
          this.animatedLeftPercentage = 0;
          this.animatedRightPercentage = 0;
          this.animatedProgressLeft = 0;
          this.animatedProgressRight = 0;
        }
        this.previousResultsVisible = newVal;
      },
      immediate: true,
    },
    'leftOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedLeftPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressLeft', oldVal || 0, newVal || 0);
        }
      },
    },
    'rightOption.resultado': {
      handler(newVal, oldVal) {
        if (this.item.flg_exibir_resultado && this.previousResultsVisible) {
          this.animateValue('animatedRightPercentage', oldVal || 0, newVal || 0);
          this.animateValue('animatedProgressRight', oldVal || 0, newVal || 0);
        }
      },
    },
  },

  methods: {
    animateValue(propertyName, fromValue, toValue) {
      const startTime = Date.now();
      const difference = toValue - fromValue;

      if (this[propertyName + 'Animation']) {
        cancelAnimationFrame(this[propertyName + 'Animation']);
      }

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);

        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        this[propertyName] = Math.round(fromValue + (difference * easeOutCubic));

        if (progress < 1) {
          this[propertyName + 'Animation'] = requestAnimationFrame(animate);
        } else {
          this[propertyName] = toValue;
          delete this[propertyName + 'Animation'];
        }
      };

      this[propertyName + 'Animation'] = requestAnimationFrame(animate);
    },
  },

  mounted() {
    if (this.item?.flg_exibir_resultado) {
      this.animatedLeftPercentage = this.leftOption.resultado || 0;
      this.animatedRightPercentage = this.rightOption.resultado || 0;
      this.animatedProgressLeft = this.leftOption.resultado || 0;
      this.animatedProgressRight = this.rightOption.resultado || 0;
      this.previousResultsVisible = true;
    } else {
      this.animatedLeftPercentage = 0;
      this.animatedRightPercentage = 0;
      this.animatedProgressLeft = 0;
      this.animatedProgressRight = 0;
      this.previousResultsVisible = false;
    }
  },

  beforeDestroy() {
    Object.keys(this.$data).forEach(key => {
      if (key.endsWith('Animation') && this[key]) {
        cancelAnimationFrame(this[key]);
      }
    });
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Fields Regular";
  src: url("@/assets/SaiaJusta/fonts/fields_regular.otf") format("opentype");
}

@font-face {
  font-family: "Fields Medium";
  src: url("@/assets/SaiaJusta/fonts/fields_medium.otf") format("opentype");
}

.card {
  border-radius: 0;
}

.card-header,
.card-footer {
  flex-shrink: 0;
}

.title-section {
  width: 100%;
}

.title-text {
  display: block;
  text-align: center;
}

.poll-layout {
  width: 100%;
}

.horizontal-poll-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: inherit;
}

.option-container {
  flex: 1 1 0;
  width: 50%;
  min-width: 0;
}

.left-container {
  align-items: flex-start;
  text-align: left;
}

.right-container {
  align-items: flex-end;
  text-align: right;
}

.progress-bar-section {
  flex: 1;
  position: relative;
  min-width: 0;
  width: 100%;
}

.option-text {
  word-break: break-word;
}

.progress-row {
  width: 100%;
  align-items: center;
}

.option-progress-bar {
  display: flex;
  width: 100%;
}

.progress-fill {
  border-radius: 0;
}

.progress-fill-right {
  margin-left: auto;
  align-self: flex-end;
}

.percentage-text {
  opacity: 1;
  flex-shrink: 0;
  flex-grow: 0;
  z-index: 3;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.1s ease-out;
}

.left-percentage {
  justify-content: flex-end;
  text-align: right;
}

.right-percentage {
  justify-content: flex-start;
  text-align: left;
}

.option-only-row {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.option-text-only {
  word-break: break-word;
}

.results-transition-enter-active,
.results-transition-leave-active {
  transition: all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.results-transition-enter-from {
  opacity: 0;
  transform: translateY(15px) scale(0.96);
}

.results-transition-leave-to {
  opacity: 0;
  transform: translateY(-15px) scale(0.96);
}

.percentage-fade-enter-active {
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 0.3s;
}

.percentage-fade-enter-from {
  opacity: 0;
  transform: scale(0.7) translateY(10px);
}

@media (max-width: 768px) {
  .horizontal-poll-container {
    flex-direction: column;
    gap: 15px;
  }

  .option-container {
    width: 100%;
    min-width: 300px;
  }
}

@media (max-height: 600px) {
  .card {
    margin-bottom: 0;
  }
}
</style>
